import clsx from "clsx";
import React, { DetailedHTMLProps, HTMLAttributes, useMemo } from "react";

import { CompanySiteType } from "../../../../types/company-site";
import { isStoreAlwaysClosed, StoreTimetable } from "../../../../utils/timetable";
import { StoreIcon } from "../store-icon";

export interface StoreListItemProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  store: Queries.CompanySiteDetailDataFragment;
  labels: {
    "store-locator-store-open": string;
    "store-locator-store-closed": string;
    "store-locator-store-opens-at": string;
    "store-locator-store-closes-at": string;
  };
  isActive?: boolean;
}

export function StoreListItem({ store, labels, className, isActive, ...rest }: StoreListItemProps) {
  const { isOpen, formattedRelativeDay, formattedTime } = useMemo(() => {
    return new StoreTimetable(store).getNextTimetableEvent();
  }, [store]);

  return (
    <div
      className={clsx(
        "pl-2 pb-4 pt-2 bg-light rounded-lg text-sm cursor-pointer",
        isActive && "ring-1 ring-primary",
        className
      )}
      {...rest}
    >
      <div className="flex justify-between items-center gap-2">
        <h4 className="font-bold">{store.name}</h4>
        {store.siteType && <StoreIcon type={store.siteType as CompanySiteType} size={40} />}
      </div>
      <div className="flex flex-col gap-1 pr-4 font-light">
        <p>{store.address}</p>
        <p>
          {[
            store.city, // required field
            [store.zipCode, store.state].filter(Boolean).join(" - "), // optional fields
          ]
            .filter(Boolean)
            .join(", ")}
        </p>
        <p>
          {!isStoreAlwaysClosed(store) && (
            <span className={clsx("inline", isOpen ? "text-[#03A400]" : "text-error-dark")}>
              {isOpen ? labels["store-locator-store-open"] : labels["store-locator-store-closed"]}
            </span>
          )}
          &nbsp;
          {formattedRelativeDay && formattedTime && (
            <span className="text-[#808285] inline">
              {(isOpen
                ? labels["store-locator-store-closes-at"]
                : labels["store-locator-store-opens-at"]
              )
                .replace("{{day}}", formattedRelativeDay ?? "")
                .replace("{{time}}", formattedTime ?? "")}
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
